import { DynamicColumn, MultiCustomAttribute, ProductItem, TableRow } from './types';
import { changedFields, clearOutOfScopeDynamicValues, clearOutOfScopeMultiValues } from './useInlineRow';
import { selectionsFromProduct, IDENTIFIER_AXIS_FIELDS } from '../CreateProduct/identifierAxisUsage';
import { PRODUCT_TYPE_CHANGE_CONFIRMED_FLAG } from '../CreateProduct/productTypeChange';

/** Ticket cap: at most this many products may be put into inline edit at one time (BM-9596). */
export const MAX_MULTI_EDIT_PRODUCTS = 20;

/**
 * What the floating action button currently does. `none` gives the slot up entirely; `draft` is the
 * multi-add bar: append a row, save every draft, cancel all.
 */
export type FloatyEditMode = 'add' | 'edit' | 'save' | 'none' | 'draft';

/** One product the server refused, with the i18n key explaining why. */
export interface BulkUpdateFailure {
  devOrderId: string;
  errMsg: string;
  /** Countries a sold-country rejection names, as the single-product save returns them. */
  removedSlugs?: string[];
  invalidSlugs?: string[];
}

/** The bulk endpoint's per-item outcome — mirrors BulkEditResult on the server. */
export interface BulkUpdateResult {
  totalRequested: number;
  successCount: number;
  failureCount: number;
  succeeded: string[];
  failed: BulkUpdateFailure[];
  /** Saved rows in list shape, returned by the save so the grid patches without a second request. */
  rows?: unknown[];
  /** Saved products whose Product Identifier rows could not be written. */
  identifierSyncFailed?: string[];
}

/** Response of the bulk edit-detail read: the products it could open, and why the rest could not. */
export interface BulkDetailResult {
  rows: ProductItem[];
  failed: BulkUpdateFailure[];
}

/**
 * Split a bulk response into the rows to close and the rows to keep open.
 *
 * A rejected row keeps its edits so the user can correct and retry; a saved row closes. Reading the
 * split off `failed` rather than `succeeded` keeps a row open if the server ever reports it in
 * neither list, which is the safe direction — it never silently discards typed work.
 *
 * @param result the bulk endpoint's response
 * A failure tied to one field is shown on that field's cell; any other failure marks the row's
 * Product Number cell, which every row renders, so the failed row is always visible.
 * @returns the ids to keep open, and the per-row error to show against each
 */
export const partitionBulkResult = (
  result: BulkUpdateResult | null | undefined,
): { openIds: Set<string>; errorsByRow: Record<string, Record<string, string>> } => {
  const failures = result?.failed ?? [];
  const openIds = new Set<string>(failures.map((entry) => String(entry.devOrderId)));
  const errorsByRow = Object.fromEntries(
    failures.map((entry) => [String(entry.devOrderId), { [fieldForSaveError(entry.errMsg) ?? 'number']: entry.errMsg }]),
  );
  return { openIds, errorsByRow };
};

/**
 * The rows whose current value differs from the baseline captured when edit opened.
 * Saving only these means selecting fifteen and editing three sends three.
 * @param editRows rowId -> row as currently edited
 * @param baselineRows rowId -> row as loaded
 * @returns rowId -> row, for changed rows only
 */
/**
 * Order a freshly-loaded list by the order captured when inline editing opened.
 *
 * A full list refresh re-applies the server sort, so changing a Product Number or Description and
 * saving moved that row to its new sorted position — while it was still open for editing, which
 * pulls the row out from under the user. Rows keep the pinned order until editing ends; products
 * the pin does not know about (a new page, a newly created product) follow in list order.
 * @param list the products as the list currently returns them
 * @param pinnedIds product ids in the order captured when editing opened
 * @returns the list reordered to the pinned order
 */
export const pinRowOrder = <T extends { _id?: unknown }>(list: T[], pinnedIds: string[] | null): T[] => {
  if (!pinnedIds || pinnedIds.length === 0) return list;
  const rank = new Map(pinnedIds.map((id, index) => [id, index]));
  const rankOf = (item: T): number => rank.get(String(item?._id)) ?? Number.MAX_SAFE_INTEGER;
  return [...list].sort((left, right) => rankOf(left) - rankOf(right));
};

export const collectDirtyRows = (
  editRows: Record<string, TableRow>,
  baselineRows: Record<string, TableRow>,
): Record<string, TableRow> =>
  Object.fromEntries(Object.entries(editRows).filter(([rowId, row]) => changedFields(row, baselineRows[rowId]).length > 0));

/**
 * Derive the floating button's mode. Save/Cancel belongs to a bulk batch alone: a single-row edit
 * commits through its own per-row tick and cross, so putting Save on the bar as well gave one row
 * two competing commit paths. While a single row is open the slot is given up entirely rather than
 * falling back to Add or Edit, both of which close the open row and discard it.
 *
 * Within a bulk batch the open rows still win over selection, so the button stays Save until the
 * user saves or cancels — even if they untick every checkbox meanwhile.
 * @param counts selected row count, open-edit row count, and whether those rows are a bulk batch
 * @returns the mode the button should render
 */
export const resolveFloatyMode = ({
  selectedCount,
  editingCount,
  isBulkEditing,
  addDraftCount = 0,
}: {
  selectedCount: number;
  editingCount: number;
  isBulkEditing: boolean;
  addDraftCount?: number;
}): FloatyEditMode => {
  if (addDraftCount > 0) return 'draft';
  if (editingCount > 0) return isBulkEditing ? 'save' : 'none';
  if (selectedCount > 0) return 'edit';
  return 'add';
};

/**
 * Whether the current selection may be opened for inline edit: at least one product and no
 * more than the cap.
 * @param selectedIds currently ticked product ids
 * @returns true when the Edit action is allowed
 */
export const isSelectionEditable = (selectedIds: string[]): boolean => selectedIds.length > 0 && selectedIds.length <= MAX_MULTI_EDIT_PRODUCTS;

/**
 * Build the keyed edit-row map for a selection. Selected ids with no matching product in the
 * current list are skipped, so a stale selection cannot open a phantom row.
 * @param selectedIds currently ticked product ids
 * @param products the loaded product list
 * @param toRow maps a product to its editable table row
 * @returns rowId -> editable row
 */
export const selectionToEditRows = (
  selectedIds: string[],
  products: ProductItem[],
  toRow: (product: ProductItem) => TableRow,
): Record<string, TableRow> => {
  const byId = new Map(products.map((product) => [String(product._id), product]));
  const rows: Record<string, TableRow> = {};
  selectedIds.forEach((id) => {
    const product = byId.get(String(id));
    if (product) rows[String(id)] = toRow(product);
  });
  return rows;
};

/** A Mongo ObjectId, which must never reach the clipboard as a cell's copied text. */
const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

/** Columns that never contribute to the clipboard: the grid wrapper's pinned row-actions column. */
export const NON_COPYABLE_COL_IDS: ReadonlySet<string> = new Set(['__cag_actions']);

/** How a multi-select cell's labels are joined for display, and so how a pasted cell splits again. */
export const CLIPBOARD_LIST_SEPARATOR = ', ';

/**
 * Clipboard COPY: the text a cell shows, rather than the value it stores.
 *
 * Reference columns hold ids, so copying the raw value would put an ObjectId on the clipboard —
 * useless in a spreadsheet, and useless pasted back into another row. Rows carry a `<field>__label`
 * companion for exactly this display purpose.
 * @param row the row being copied from
 * @param colId the column id
 * @param value the raw cell value
 * @returns the display text for the clipboard
 */
export const clipboardCellText = (row: TableRow | undefined, colId: string, value: unknown): string => {
  /* The grid wrapper's pinned actions column (rendered under the "Show Status" header) holds row
     buttons, not data. Nothing can be pasted into it — it is not editable — but a range dragged
     across it would otherwise copy whatever it renders, so it contributes nothing. */
  if (NON_COPYABLE_COL_IDS.has(colId)) return '';
  const label = row?.[`${colId}__label`];
  if (label !== undefined && label !== null && label !== '') return String(label);
  if (Array.isArray(value)) {
    const parts = value.map((entry) => String(entry ?? ''));
    /* Belt and braces: with no label to copy, an id-backed cell would put ObjectIds on the
       clipboard — meaningless in a spreadsheet, and unresolvable on paste, which then CLEARS the
       target. Copy nothing instead of copying something guaranteed to destroy the cell it lands in. */
    if (parts.some((part) => OBJECT_ID_PATTERN.test(part))) return '';
    return parts.join(CLIPBOARD_LIST_SEPARATOR);
  }
  return value === undefined || value === null ? '' : String(value);
};

/**
 * Clipboard PASTE: turn pasted text back into the value the payload builder expects.
 *
 * For a reference column that means resolving the label to its id. Text matching no option yields
 * an empty cell rather than the text itself: writing a NAME into an id-backed field is what makes
 * dependent lookups fire with "Adidas" where an id belongs, and a visibly empty cell is better
 * feedback than a value that looks accepted and fails on save.
 *
 * A multi-select cell holds an ARRAY, and copy joined its labels with ", ". Treating that as one
 * label looked for an option literally named "Red, Blue", found none, and wiped the cell; a single
 * value came back as a bare string, which renders no chips and reaches the payload as a string
 * where an array belongs. So a multi column splits on the separator and resolves each part.
 *
 * Columns with no option list are free text, so the text passes through unchanged.
 * @param options the column's option list, if it is a reference column
 * @param text the pasted text
 * @param isMulti whether the column stores an array of values
 * @returns the value to store
 */
export const clipboardCellValue = (options: Array<{ _id?: unknown; name?: unknown }> | undefined, text: unknown, isMulti = false): unknown => {
  const raw = text === undefined || text === null ? '' : String(text);
  if (!Array.isArray(options) || !options.length) return isMulti ? [] : raw;
  const resolve = (label: string): string => {
    const wanted = label.trim().toLowerCase();
    if (!wanted) return '';
    const match = options.find((option) => String(option?.name ?? '').toLowerCase() === wanted);
    return match ? String(match._id ?? '') : '';
  };
  if (!isMulti) return resolve(raw);
  return raw
    .split(CLIPBOARD_LIST_SEPARATOR)
    .map(resolve)
    .filter(Boolean);
};

/**
 * Refresh the `<field>__label` companions for every field an edit changed.
 *
 * Resolved through the SAME id→name maps rowMapper uses, never through `selectOptions`. Several
 * option lists are scoped to one row — the factory list to that row's suppliers, the size list to
 * its size range — so resolving another row's values against them produced an EMPTY label. Copy
 * then fell through to joining the raw ids, and pasting those ids matched no option and cleared
 * the target cell. `nameMaps` overrides exactly those fields with global maps for this reason.
 * @param before the row as it was
 * @param after the row after the edit and its cascades
 * @param nameMaps field -> (id -> display name), as built for rowMapper
 * @returns the row with its labels back in step with its values
 */
export const withRefreshedLabels = (before: TableRow, after: TableRow, nameMaps: Record<string, Record<string, string> | undefined>): TableRow => {
  const patched: TableRow = { ...after };
  Object.keys(after).forEach((field) => {
    if (field.endsWith('__label')) return;
    const nameById = nameMaps?.[field];
    if (!nameById) return;
    if (JSON.stringify(after[field] ?? null) === JSON.stringify(before[field] ?? null)) return;
    const values = Array.isArray(after[field]) ? (after[field] as unknown[]) : [after[field]];
    patched[`${field}__label`] = values
      .map((value) => nameById[String(value ?? '')] ?? '')
      .filter(Boolean)
      .join(CLIPBOARD_LIST_SEPARATOR);
  });
  return patched;
};

/**
 * Fields whose values can be locked by downstream use, mapped to the field on the product detail
 * that reports which ids are locked.
 *
 * `GET /development-orders/:doId` returns `orderSupplierId` (suppliers reached by a Purchase
 * Order, costing or sample), `orderColors` (colourways reached by a BOM, sample, costing or design)
 * and `orderFactoryId` (factories reached by a Purchase Order, costing, sample or critical path,
 * from `getFactoriesInUseForProduct`). Single edit uses the same fields — see the
 * `inUseSupplierIds` / `inUseColourIds` / `inUseFactoryIds` memos in CreateProduct.
 */
export const IN_USE_LOCK_FIELDS: Readonly<Record<string, string>> = {
  suppliers: 'orderSupplierId',
  colourLibraries: 'orderColors',
  supplierFactories: 'orderFactoryId',
};

/**
 * The ids a row may not deselect for a given field, read off the hydrated product detail the row
 * was built from.
 * @param row the grid row (carries the detail as `__product`)
 * @param field the row field being edited
 * @returns locked ids as strings; empty when the field has no lock or the row is not hydrated
 */
export const lockedIdsForField = (row: TableRow | undefined, field: string): ReadonlySet<string> => {
  const sourceField = IN_USE_LOCK_FIELDS[field];
  if (!sourceField || !row) return new Set();
  const product = row.__product as Record<string, unknown> | undefined;
  const locked = product?.[sourceField];
  if (!Array.isArray(locked)) return new Set();
  return new Set(locked.map((id) => String(id)));
};

/**
 * Re-add any locked id the incoming value dropped.
 *
 * Disabling the option in the menu stops a click removing it, but not every route in: a fill drag,
 * a paste, or a "Clear All" all set the whole array at once. This is the single chokepoint every
 * one of those passes through, so the lock holds regardless of how the value arrived.
 * @param row the row being edited
 * @param field the field being written
 * @param nextValue the value the edit proposes
 * @returns the value with locked ids restored, or `nextValue` untouched when nothing is locked
 */
export const preserveLockedIds = (row: TableRow | undefined, field: string, nextValue: unknown): unknown => {
  const locked = lockedIdsForField(row, field);
  if (!locked.size) return nextValue;
  const proposed = (Array.isArray(nextValue) ? nextValue : []).map((id) => String(id));
  const missing = [...locked].filter((id) => !proposed.includes(id));
  return missing.length ? [...proposed, ...missing] : nextValue;
};

/**
 * The selection id a product is ticked under: its cluster id when the row is showing a reorder in
 * place, otherwise its own id. Mirrors the card list, so both views share one selection shape.
 * @param product the product behind the row
 * @returns the id held in `selectedIds`
 */
export const productSelectionId = (product: ProductItem): string => String((product as { clusterId?: string }).clusterId ?? product._id);

/**
 * The reorder (cluster) children of one product.
 *
 * Read from the lazily-loaded full reorder map when it holds them, and from the product's own
 * `reOrders` otherwise, so the cluster is known before a row has ever been expanded. The reorder
 * query returns the parent alongside its children, so the parent is filtered out.
 *
 * @param product the cluster parent
 * @param reorderFullById full reorder objects by parent id, where already fetched
 * @returns the child product ids
 */
export const clusterChildIds = (product: ProductItem, reorderFullById: Readonly<Record<string, ProductItem[]>> = {}): string[] => {
  const parentId = String(product?._id ?? '');
  if (!parentId) return [];
  const loaded = reorderFullById[parentId];
  const children = (loaded && loaded.length ? loaded : product?.reOrders || []) as ProductItem[];
  return children.map((child) => String(child?._id ?? '')).filter((id) => id && id !== parentId);
};

/**
 * Expand a checkbox selection into the products it covers.
 *
 * Cluster children carry their own checkbox, so a selection normally already lists them and this
 * is a no-op. It still matters as a backstop: ticking a parent cascades to children that are
 * loaded at that moment, and a cluster whose reorders arrive later would otherwise be undercounted
 * until the user re-ticked it.
 *
 * @param selectedIds ids currently ticked
 * @param products the loaded product list
 * @param reorderFullById full reorder objects by parent id, where already fetched
 * @returns every product id in the selection, parents first, de-duplicated
 */
export const expandSelectionToProducts = (
  selectedIds: readonly string[],
  products: readonly ProductItem[],
  reorderFullById: Readonly<Record<string, ProductItem[]>> = {},
): string[] => {
  const ticked = new Set(selectedIds.map(String));
  const expanded: string[] = [];
  const seen = new Set<string>();
  const matched = new Set<string>();

  const add = (id: unknown) => {
    const key = String(id ?? '');
    if (!key || seen.has(key)) return;
    seen.add(key);
    expanded.push(key);
  };

  products.forEach((product) => {
    const selectionId = productSelectionId(product);
    if (!ticked.has(selectionId)) return;
    matched.add(selectionId);
    add(product._id);
    clusterChildIds(product, reorderFullById).forEach(add);
  });

  /*
   * A ticked id matching no top-level product is a cluster child selected on its own — its row is
   * nested under a parent, so it never appears in the list this walks. Counting only what the walk
   * finds reported an independently ticked child as nothing selected at all.
   *
   * Ids that DID match are skipped here: a row ticked under its clusterId is already represented
   * by the product's real _id, and adding the clusterId too would count a value that is not a
   * product.
   */
  selectedIds.forEach((id) => {
    if (!matched.has(String(id))) add(id);
  });

  return expanded;
};

/**
 * Fields whose change destroys dependent data, mapped to the i18n suffix naming the field.
 *
 * These are the four the single-edit page guards behind a confirmation. Each one triggers server
 * work beyond the product row itself — see `collectDestructiveChanges` for what each destroys.
 */
export const DESTRUCTIVE_FIELDS: Readonly<Record<string, string>> = {
  supplierType: 'PRODUCT_TYPE',
  brandId: 'BRAND',
  subBrandId: 'SUB_BRAND',
  catOneId: 'CATEGORY_ONE',
  catTwoId: 'CATEGORY_TWO',
  catThreeId: 'CATEGORY_THREE',
  supplierFactories: 'FACTORY',
};

/**
 * Fields the single-edit page guards for one reason only: moving the product out of the scope a
 * grading group covers. These are its `GRADING_SCOPE_KEYS`, which it tests before any
 * field-specific handling, so a product carrying no grading group is never asked about them.
 */
export const GRADING_SCOPE_FIELDS: readonly string[] = ['subBrandId', 'catOneId', 'catTwoId', 'catThreeId'];

/**
 * Fields whose warning depends on server state (DAM entries, BOM rows) that the grid does not
 * hold, so the decision cannot be made here — `collectDestructiveChanges` keeps every changed
 * product and the caller narrows them once the checks have run.
 */
export const SERVER_CHECKED_DESTRUCTIVE_FIELDS: readonly string[] = ['supplierType', 'brandId'];

/** One destructive field, and every product in the batch whose value for it changed. */
export interface DestructiveChange {
  field: string;
  /** i18n suffix for the field's display name. */
  labelKey: string;
  productIds: string[];
}

/**
 * Whether a row's value for one field differs from its baseline.
 * @param row the edited row
 * @param baseline the row as it was when the batch opened
 * @param field the field to compare
 * @returns true when the value changed
 */
export const isFieldChanged = (row: TableRow | undefined, baseline: TableRow | undefined, field: string): boolean => {
  if (!row || !baseline) return false;
  return JSON.stringify(row[field] ?? null) !== JSON.stringify(baseline[field] ?? null);
};

/** A grading group as held on a product row: `{ value }` is the grading library id. */
interface RowGradingGroup {
  value?: unknown;
}

/**
 * The grading groups a row carries.
 *
 * They are read off the product the row was built from rather than the row itself: the row is the
 * save payload's source, and `buildProductPayload` sends `gradingGroups` whenever the key is
 * present — adding it to every row would post an empty list for products that never had one.
 *
 * @param row the row to read
 * @returns the grading groups, or an empty list when the row carries none
 */
const rowGradingGroups = (row: TableRow | undefined): RowGradingGroup[] => {
  const groups = (row?.gradingGroups ?? row?.__product?.gradingGroups) as RowGradingGroup[] | undefined;
  return Array.isArray(groups) ? groups : [];
};

/**
 * Whether the row carried a grading group before the edit. A product with none cannot lose one,
 * which is the whole reason the sub-brand and category warnings exist.
 * @param baseline the row as it was when the edit opened
 * @returns true when at least one grading group is present
 */
const hasGradingGroups = (baseline: TableRow | undefined): boolean => rowGradingGroups(baseline).length > 0;

/**
 * Whether the edit takes a factory away. Adding one strands nothing, so single edit only warns on
 * removal and the grid now matches.
 * @param row the edited row
 * @param baseline the row as it was when the edit opened
 * @returns true when a factory present in the baseline is no longer selected
 */
const removesFactory = (row: TableRow | undefined, baseline: TableRow | undefined): boolean => {
  const after = new Set(((row?.supplierFactories as unknown[]) ?? []).map(String));
  return ((baseline?.supplierFactories as unknown[]) ?? []).some((factoryId) => !after.has(String(factoryId)));
};

/**
 * Whether changing this field on this row actually destroys anything.
 *
 * A changed value is not on its own a reason to warn — single edit checks what the product holds
 * first, and only asks when something would really be lost. This reproduces the checks that can be
 * answered from the row itself; Product Type and Brand additionally depend on DAM and BOM state
 * held only on the server, so they stay candidates here and the caller narrows them. A Sub Brand
 * emptied by a Brand change is part of that Brand change and is not asked about on its own, as
 * single edit clears it without a grading-scope warning.
 *
 * @param field the destructive field being tested
 * @param row the edited row
 * @param baseline the row as it was when the edit opened
 * @returns true when the change warrants a confirmation
 */
export const changeDestroysData = (field: string, row: TableRow | undefined, baseline: TableRow | undefined): boolean => {
  if (!isFieldChanged(row, baseline, field)) return false;
  if (field === 'subBrandId' && !row?.subBrandId && isFieldChanged(row, baseline, 'brandId')) return false;
  if (GRADING_SCOPE_FIELDS.includes(field)) return hasGradingGroups(baseline);
  if (field === 'supplierFactories') return removesFactory(row, baseline);
  return true;
};

/**
 * Group a batch's destructive changes by field.
 *
 * Single edit asks once per change, which does not scale to a batch: twenty rows could raise twenty
 * dialogs. Grouping by field asks once per *kind* of change and names how many products it hits, so
 * one confirmation covers "Product Type on 3 products" however many rows are involved.
 *
 * Only dirty rows are considered — a row opened because its cluster parent was ticked, and never
 * touched, destroys nothing.
 *
 * @param dirtyRows the rows that will be saved
 * @param baselineRows the same rows as they were when the batch opened
 * @returns one entry per destructive field that changed, in DESTRUCTIVE_FIELDS order
 */
export const collectDestructiveChanges = (
  dirtyRows: Record<string, TableRow>,
  baselineRows: Record<string, TableRow>,
): DestructiveChange[] =>
  Object.entries(DESTRUCTIVE_FIELDS)
    .map(([field, labelKey]) => ({
      field,
      labelKey,
      productIds: Object.keys(dirtyRows).filter((rowId) => changeDestroysData(field, dirtyRows[rowId], baselineRows[rowId])),
    }))
    .filter((change) => change.productIds.length > 0);

/**
 * Local field resets that accompany a destructive change, mirroring the single-edit confirm
 * handlers. Applied to the rows before they are sent, so a saved product does not keep values that
 * belonged to the old Product Type or Brand.
 *
 * A Brand change clears Sub Brand, and clears the size selection only when the new brand strands a
 * grading group — exactly when single edit's Brand confirmation clears them. Sub Brand and category
 * changes reset nothing here: single edit leaves the sizes alone and the server drops the grading
 * groups the new scope no longer covers. A value the user picked again after the Brand change is
 * kept. Custom attribute values the new Product Type or Brand no longer admits are blanked exactly
 * as the single-edit cascade blanks them.
 *
 * @param dirtyRows the rows about to be saved
 * @param baselineRows the same rows as they were when the batch opened
 * @param attributes the custom-attribute master, used to decide which values fell out of scope
 * @param multiAttributes the multi-select custom-attribute master, used the same way on a Product Type change
 * @param gradingLibrary the grading master, used to tell whether a Brand change strands a grading group
 * A Product Type change leaves the suppliers alone (they are cleared when the new type is picked, so
 * any chosen afterwards are kept) and stamps the confirmation flag, so the server removes the
 * dependent data on save.
 * @returns the rows with dependent fields reset where a destructive field changed
 */
export const applyDestructiveCascades = (
  dirtyRows: Record<string, TableRow>,
  baselineRows: Record<string, TableRow>,
  attributes: readonly DynamicColumn[] = [],
  multiAttributes: readonly MultiCustomAttribute[] = [],
  gradingLibrary: readonly GradingLibraryEntry[] = [],
): Record<string, TableRow> => {
  const next: Record<string, TableRow> = {};
  Object.entries(dirtyRows).forEach(([rowId, row]) => {
    const baseline = baselineRows[rowId];
    let updated: TableRow = row;
    if (isFieldChanged(row, baseline, 'supplierType')) {
      const confirmedChange = row.__isDraft ? updated : { ...updated, [PRODUCT_TYPE_CHANGE_CONFIRMED_FLAG]: true };
      updated = clearOutOfScopeMultiValues(clearOutOfScopeDynamicValues(confirmedChange, 'supplierType', attributes), multiAttributes);
    }
    if (isFieldChanged(row, baseline, 'brandId')) {
      const untouched = (field: string) => !isFieldChanged(updated, baseline, field);
      const losesGrading = gradingImpactOfBrandChange(row, baseline, gradingLibrary).losesSome;
      updated = clearOutOfScopeDynamicValues(
        {
          ...updated,
          ...(untouched('subBrandId') ? { subBrandId: '' } : {}),
          ...(losesGrading && untouched('sizeRangeId') ? { sizeRangeId: [] } : {}),
          ...(losesGrading && untouched('sizeRangeSizes') ? { sizeRangeSizes: [] } : {}),
        },
        'brandId',
        attributes,
      );
    }
    next[rowId] = updated;
  });
  return next;
};

/** A grading library entry, carrying the brands it applies to. */
export interface GradingLibraryEntry {
  _id?: unknown;
  brand?: Array<{ _id?: unknown } | string>;
}

/**
 * The grading groups on a row that survive a move to `newBrandId`.
 *
 * Mirrors the filter single edit runs before it decides whether to warn: a grading group survives
 * when its library entry lists the new brand. A group whose library entry cannot be found does not
 * survive — the same conservative call single edit makes.
 *
 * @param row the row being changed
 * @param newBrandId the brand being moved to
 * @param gradingLibrary the grading master (PomDetails.GradingList)
 * @returns the surviving groups
 */
export const compatibleGradingGroups = (
  row: TableRow | undefined,
  newBrandId: string,
  gradingLibrary: readonly GradingLibraryEntry[] = [],
): RowGradingGroup[] => {
  const groups = rowGradingGroups(row);
  if (!groups.length) return [];
  return groups.filter((group) => {
    const entry = gradingLibrary.find((item) => String(item?._id) === String(group?.value));
    if (!entry || !Array.isArray(entry.brand)) return false;
    return entry.brand.some((brand) => String(typeof brand === 'string' ? brand : brand?._id) === String(newBrandId));
  });
};

/**
 * Whether a brand change on this row costs it grading groups, and whether it loses all of them.
 *
 * Single edit only raises its Brand dialog when at least one group would be lost, and only wipes
 * grading server-side when none survives. Both decisions come from here so the batch asks — and
 * destroys — on exactly the same terms.
 *
 * @param row the edited row
 * @param baseline the row as it was when the batch opened
 * @param gradingLibrary the grading master
 * @returns whether any group is lost, and whether every group is lost
 */
export const gradingImpactOfBrandChange = (
  row: TableRow | undefined,
  baseline: TableRow | undefined,
  gradingLibrary: readonly GradingLibraryEntry[] = [],
): { losesSome: boolean; losesAll: boolean } => {
  const groups = rowGradingGroups(baseline);
  if (!groups.length) return { losesSome: false, losesAll: false };
  const surviving = compatibleGradingGroups(baseline, String(row?.brandId ?? ''), gradingLibrary);
  return { losesSome: surviving.length < groups.length, losesAll: surviving.length === 0 };
};

/**
 * The destructive changes a duplicate draft has to confirm before it is sent.
 *
 * A draft is not a saved product: it has no DAM entries, BOM or factory usage to clean up, so the
 * server-checked fields never apply to it. What it does carry is the source product's grading data,
 * which a Brand, Sub Brand or category change can push out of scope exactly as on a saved product.
 * The brand change is narrowed to rows that actually lose a group, as the server-state check does
 * for saved rows; the grading-scope fields already require a group to be present.
 *
 * @param dirtyRows rowId -> draft row about to be sent
 * @param baselineRows rowId -> the draft as it was when it opened, holding the source's values
 * @param gradingLibrary the grading master (PomDetails.GradingList)
 * @returns the changes to confirm, in dialog order
 */
export const collectDraftDestructiveChanges = (
  dirtyRows: Record<string, TableRow>,
  baselineRows: Record<string, TableRow>,
  gradingLibrary: readonly GradingLibraryEntry[] = [],
): DestructiveChange[] =>
  collectDestructiveChanges(dirtyRows, baselineRows)
    .filter((change) => change.field === 'brandId' || GRADING_SCOPE_FIELDS.includes(change.field))
    .map((change) =>
      change.field !== 'brandId'
        ? change
        : {
            ...change,
            productIds: change.productIds.filter(
              (rowId) => gradingImpactOfBrandChange(dirtyRows[rowId], baselineRows[rowId], gradingLibrary).losesSome,
            ),
          },
    )
    .filter((change) => change.productIds.length > 0);

/**
 * Fill-handle drag: the value to write into one target cell.
 *
 * AG Grid infers an `object` cell data type for every array column, and that type's built-in
 * `valueParser` returns null. Its default fill formats the source value and re-parses it for the
 * target, so dragging a multi-select cell (suppliers, colourways, sizes, variables) down wrote
 * NULL into every cell it crossed instead of copying the selection. Returning the value from the
 * fill callback skips that parse. A multi-value column gets a copy of the source array, cycling
 * through the dragged range like AG Grid does; every other column returns `false`, which keeps
 * AG Grid's default copy/series fill for text, numbers and single selects untouched.
 * @param initialValues the values in the range the drag started from
 * @param currentIndex the index of the target cell within the fill
 * @param isMulti whether the column stores an array of values
 * @returns the array to write, or `false` to leave the fill to AG Grid
 */
export const fillCellValue = (initialValues: unknown[], currentIndex: number, isMulti = false): unknown[] | false => {
  if (!isMulti) return false;
  const source = initialValues.length ? initialValues[currentIndex % initialValues.length] : [];
  return Array.isArray(source) ? [...source] : [];
};

/**
 * Build a runner that lets one task through at a time and drops any call made while that task is
 * still in flight.
 *
 * An inline save can take many seconds — a duplicate copies measurements, BOM and documents — and
 * a second click on Save during that wait sent the same copy again, which then failed on the
 * product number the first request had just created. React state cannot close that gap on its
 * own: two clicks in the same tick both read the old value, so the flag lives outside render.
 * @returns a function that runs the task and resolves true, or resolves false when it was dropped
 */
export const createExclusiveRunner = (): ((task: () => Promise<void>) => Promise<boolean>) => {
  let busy = false;
  return async (task) => {
    if (busy) return false;
    busy = true;
    try {
      await task();
      return true;
    } finally {
      busy = false;
    }
  };
};

/** The warning a sub-brand or category change raises: one shared warning, as on the product page. */
export const GRADING_SCOPE_WARNING = 'gradingScope';

/** rowId -> the warnings the user already accepted for that row while it has been open. */
export type AcknowledgedByRow = Readonly<Record<string, readonly string[]>>;

/** A warning the user accepted for one row. */
export interface AcknowledgedWarning {
  rowId: string;
  warning: string;
}

/** Dialogs raised by one edit or one save, with what to do once they are all accepted or one is cancelled. */
export interface ConfirmationGroup {
  id: number;
  changes: DestructiveChange[];
  onAccepted: () => void;
  onCancelled: () => void;
}

/**
 * The warning a destructive field raises. Sub Brand and the categories share one, so a Category 1
 * change that also resets Category 2 and 3 asks once.
 * @param field the destructive field
 * @returns the warning it raises
 */
export const warningOf = (field: string): string => (GRADING_SCOPE_FIELDS.includes(field) ? GRADING_SCOPE_WARNING : field);

/**
 * Every row-and-warning pair the changes cover.
 * @param changes the destructive changes
 * @returns one entry per affected row per warning
 */
export const warningsOf = (changes: readonly DestructiveChange[]): AcknowledgedWarning[] =>
  changes.flatMap((change) => change.productIds.map((rowId) => ({ rowId, warning: warningOf(change.field) })));

/**
 * Drop the products whose warning the user already accepted.
 * @param changes the destructive changes
 * @param acknowledged rowId -> the warnings accepted for that row
 * @returns the changes still needing a confirmation, empty ones removed
 */
export const unacknowledgedChanges = (changes: readonly DestructiveChange[], acknowledged: AcknowledgedByRow): DestructiveChange[] =>
  changes
    .map((change) => ({ ...change, productIds: change.productIds.filter((rowId) => !acknowledged[rowId]?.includes(warningOf(change.field))) }))
    .filter((change) => change.productIds.length > 0);

/**
 * Keep only what one edit introduced: the fields that edit actually changed, on rows whose warning
 * has not been accepted yet. Without this, editing an unrelated cell would ask again about a
 * Product Type change accepted earlier.
 * @param changes the row's destructive changes against its baseline
 * @param before rowId -> row before the edit
 * @param after rowId -> row after the edit
 * @param acknowledged rowId -> the warnings accepted for that row
 * @returns the changes the edit introduced, empty ones removed
 */
export const changesIntroducedBy = (
  changes: readonly DestructiveChange[],
  before: Record<string, TableRow>,
  after: Record<string, TableRow>,
  acknowledged: AcknowledgedByRow,
): DestructiveChange[] =>
  unacknowledgedChanges(changes, acknowledged)
    .map((change) => ({ ...change, productIds: change.productIds.filter((rowId) => isFieldChanged(after[rowId], before[rowId], change.field)) }))
    .filter((change) => change.productIds.length > 0);

/**
 * Collapse changes raising the same warning into one dialog covering all their products.
 * @param changes the destructive changes
 * @returns one change per warning, in first-seen order
 */
export const mergeByWarning = (changes: readonly DestructiveChange[]): DestructiveChange[] => {
  const merged = new Map<string, DestructiveChange>();
  changes.forEach((change) => {
    const warning = warningOf(change.field);
    const existing = merged.get(warning);
    merged.set(warning, existing ? { ...existing, productIds: [...new Set([...existing.productIds, ...change.productIds])] } : change);
  });
  return [...merged.values()];
};

/**
 * Decide which dialogs one edit raises. A Product Type or Brand dialog already covers the category
 * and sub-brand resets that change cascades, so those rows are not asked about grading scope a
 * second time; the warning is still recorded so Save does not ask either.
 * @param changes the destructive changes the edit introduced, narrowed to what really destroys data
 * @returns the dialogs to raise, and every warning to record once they are accepted
 */
export const planEditConfirmation = (changes: readonly DestructiveChange[]): { toAsk: DestructiveChange[]; warnings: AcknowledgedWarning[] } => {
  const coveredRows = new Set(changes.filter((change) => ['supplierType', 'brandId'].includes(change.field)).flatMap((change) => change.productIds));
  const toAsk = changes
    .map((change) =>
      warningOf(change.field) === GRADING_SCOPE_WARNING ? { ...change, productIds: change.productIds.filter((rowId) => !coveredRows.has(rowId)) } : change,
    )
    .filter((change) => change.productIds.length > 0);
  return { toAsk: mergeByWarning(toAsk), warnings: warningsOf(changes) };
};

/**
 * Undo one edit on a row: every key the edit changed goes back to its earlier value, unless it has
 * been changed again since, so a later edit is never thrown away.
 * @param current the row as it is now
 * @param before the row before the edit
 * @param after the row right after the edit
 * @returns the row with the edit undone
 */
export const revertEdit = (current: TableRow, before: TableRow, after: TableRow): TableRow => {
  const restored: TableRow = { ...current };
  new Set([...Object.keys(before), ...Object.keys(after)]).forEach((key) => {
    if (JSON.stringify(after[key]) === JSON.stringify(before[key])) return;
    if (JSON.stringify(current[key]) !== JSON.stringify(after[key])) return;
    if (key in before) restored[key] = before[key];
    else delete restored[key];
  });
  return restored;
};

/** Fields a cell edit writes that feed the Product Identifier axes: Size Range, Size, Colourway, Variable 1 and Variable 2. */
export const IDENTIFIER_EDIT_FIELDS: readonly string[] = [...IDENTIFIER_AXIS_FIELDS, 'additionalVariables'];

/**
 * Whether a cell edit to this field has to be reviewed the moment it is made, as the product page
 * does: either it is a destructive field itself, or it moves a Product Identifier axis.
 * @param field the field the edit wrote
 * @returns true when the edit needs the edit-time check
 */
export const isEditReviewedField = (field: string): boolean => field in DESTRUCTIVE_FIELDS || IDENTIFIER_EDIT_FIELDS.includes(field);

/**
 * The Product Identifier confirmation one edit introduces. Read from the rows as they will be saved,
 * so a Brand change that clears the sizes, or a scope change that drops Variable 2, is asked about as
 * surely as a hand edit to the axis itself. Only products whose axes this edit moved, and whose
 * warning has not been accepted yet, are kept; the caller still asks the server which of them really
 * hold identifier rows the new selection would drop.
 * @param savedBefore rowId -> the row before the edit, as it would be saved
 * @param savedAfter rowId -> the row after the edit, as it would be saved
 * @param baselineRows rowId -> the row as it was when the edit opened
 * @param acknowledged rowId -> the warnings accepted for that row
 * @returns one entry naming the candidate products, or nothing
 */
export const identifierChangesIntroducedBy = (
  savedBefore: Record<string, TableRow>,
  savedAfter: Record<string, TableRow>,
  baselineRows: Record<string, TableRow>,
  acknowledged: AcknowledgedByRow,
): DestructiveChange[] =>
  unacknowledgedChanges(collectIdentifierChanges(savedAfter, baselineRows), acknowledged)
    .map((change) => ({
      ...change,
      productIds: change.productIds.filter((rowId) => identifierSelectionsKey(savedAfter[rowId]) !== identifierSelectionsKey(savedBefore[rowId])),
    }))
    .filter((change) => change.productIds.length > 0);

/**
 * Reduce a select cell's value to the ids a row holds: an option object becomes its `_id` (or
 * `value`), and a multi-select's array is reduced element by element.
 * @param raw the value the select emitted
 * @returns the id, or the array of ids
 */
export const optionIdsOf = (raw: unknown): unknown => {
  const idOf = (entry: unknown): unknown =>
    entry && typeof entry === 'object' ? (entry as { _id?: unknown; value?: unknown })._id ?? (entry as { value?: unknown }).value : entry;
  if (Array.isArray(raw)) return raw.map(idOf);
  if (raw && typeof raw === 'object') return idOf(raw) ?? '';
  return raw;
};

/** Server failure keys that name one field, so a save error is shown on the cell the user must fix. */
const SAVE_ERROR_FIELDS: Readonly<Record<string, string>> = {
  PRODUCT_NUMBER_ALREADY_EXISTS: 'number',
  SOLD_COUNTRY_REMOVAL_NOT_ALLOWED: 'soldCountries',
  PRODUCT_TYPE_CHANGE_REQUIRES_CONFIRMATION: 'supplierType',
};

/**
 * The cell a server save failure belongs to, matched on the last segment of its translation key.
 * @param errMsg the failure key the server returned
 * @returns the field to mark, or undefined when the failure is not about a single field
 */
export const fieldForSaveError = (errMsg: string): string | undefined => SAVE_ERROR_FIELDS[String(errMsg ?? '').split('.').pop() ?? ''];

/** How many products one destructive-change check asks the server about at a time. */
export const SERVER_CHECK_CONCURRENCY = 5;

/**
 * Map items through an async task with at most `limit` tasks running at once, keeping input order.
 * @param items the inputs
 * @param limit the most tasks allowed in flight
 * @param task the async work per item
 * @returns the results, in the same order as the inputs
 */
export const mapWithConcurrency = async <T, R>(items: readonly T[], limit: number, task: (_item: T) => Promise<R>): Promise<R[]> => {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;
  const runWorker = async (): Promise<void> => {
    if (nextIndex >= items.length) return;
    const index = nextIndex;
    nextIndex += 1;
    results[index] = await task(items[index] as T);
    await runWorker();
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, runWorker));
  return results;
};

/** Pseudo-field naming the Product Identifier confirmation in the destructive-change queue. */
export const IDENTIFIER_CHANGE_FIELD = 'productIdentifier';

/**
 * A stable fingerprint of the values a product's Product Identifier rows are built from — Colourway,
 * Size, Variable 1 and Variable 2 options — so two rows compare equal regardless of selection order.
 * @param row the row to read
 * @returns the fingerprint
 */
const identifierSelectionsKey = (row: TableRow | undefined): string =>
  Object.values(selectionsFromProduct(row))
    .map((ids) => [...ids].sort().join(','))
    .join('_');

/**
 * Products whose save would change a Product Identifier axis, as one destructive-change entry.
 *
 * Read from the rows as they will be SAVED (after the destructive cascades), because a cascade such
 * as a Brand change clearing the sizes deletes identifier rows just as surely as a hand edit. Only
 * candidates are returned; the caller asks the server which of them really hold identifier rows
 * that the new selection would drop, exactly as CreateProduct's warning does.
 * @param rows the rows about to be saved
 * @param baselineRows the same rows as they were when the edit opened
 * @returns one entry naming every candidate product, or nothing when no axis changed
 */
export const collectIdentifierChanges = (rows: Record<string, TableRow>, baselineRows: Record<string, TableRow>): DestructiveChange[] => {
  const productIds = Object.keys(rows).filter(
    (rowId) => baselineRows[rowId] && identifierSelectionsKey(rows[rowId]) !== identifierSelectionsKey(baselineRows[rowId]),
  );
  return productIds.length ? [{ field: IDENTIFIER_CHANGE_FIELD, labelKey: 'PRODUCT_IDENTIFIER', productIds }] : [];
};

/**
 * The products a save stored but whose Product Identifier rows could not be written, labelled for the warning.
 * @param result the bulk endpoint's response
 * @param rows rowId -> the rows that were sent, for the product number the warning names
 * @returns one label per affected product, its product number or else its id
 */
export const identifierSyncFailedLabels = (result: BulkUpdateResult | null | undefined, rows: Record<string, TableRow>): string[] =>
  (result?.identifierSyncFailed ?? []).map((devOrderId) => String(rows[devOrderId]?.number || devOrderId));

/**
 * Clipboard COPY for a dropdown / radio custom attribute: the live name of the option the cell holds.
 * The row's `__label` is only what the product loaded with, so after an edit it names the old option;
 * an option missing from the active list (a saved, since-deactivated value) still copies that label.
 * @param row the row being copied
 * @param colId the `dyn_<id>` column
 * @param value the option id the cell stores
 * @param options the attribute's active options, when it is a dropdown / radio
 * @returns the text for the clipboard
 */
export const customAttributeClipboardText = (
  row: TableRow | undefined,
  colId: string,
  value: unknown,
  options: Array<{ _id?: unknown; name?: unknown }> | undefined,
): string => {
  const liveName = options?.find((option) => String(option._id ?? '') === String(value ?? ''))?.name;
  return liveName ? String(liveName) : clipboardCellText(row, colId, value);
};

/**
 * Reduce a cell's edited value to the ids the row holds: option objects (`{ _id }` or `{ value }`)
 * become their id, arrays of them become arrays of ids, and plain values pass through unchanged.
 * @param raw the value a cell or a fill handed over
 * @returns the value to store on the row
 */
export const normaliseCellValue = (raw: unknown): unknown => {
  if (Array.isArray(raw))
    return raw.map((entry) => (entry && typeof entry === 'object' ? (entry as { _id?: unknown; value?: unknown })._id ?? (entry as { value?: unknown }).value : entry));
  if (raw && typeof raw === 'object') return (raw as { _id?: unknown; value?: unknown })._id ?? (raw as { value?: unknown }).value ?? '';
  return raw;
};

/** Ticket cap (BM-9594): at most this many new products may be drafted and saved at once. */
export const MAX_MULTI_ADD_PRODUCTS = 15;

/** The bulk-create endpoint's per-row outcome, keyed by the draft id the client sent. */
export interface BulkCreateResult {
  totalRequested: number;
  successCount: number;
  failureCount: number;
  succeeded: Array<{ clientRowId: string; devOrderId: string }>;
  failed: Array<{ clientRowId: string; errMsg: string; field?: string }>;
}

/**
 * Split a bulk-create response into the drafts to close and the error to show on each rejected one.
 *
 * Only drafts the server confirms as created close. A draft it reports in neither list stays open,
 * so typed work is never discarded on an ambiguous answer — and drafts that were not part of this
 * save (a single-row Save) are never touched. Each error sits on the field the server names, and on
 * Product Number when it names none (a duplicate number, or a create that failed outright).
 * @param result the bulk-create endpoint's response
 * @returns the draft ids that were created, and the per-row error keyed by draft id
 */
export const partitionBulkCreateResult = (
  result: BulkCreateResult | null | undefined,
): { createdIds: Set<string>; errorsByRow: Record<string, Record<string, string>> } => {
  const createdIds = new Set((result?.succeeded ?? []).map((entry) => String(entry.clientRowId)));
  const errorsByRow = Object.fromEntries((result?.failed ?? []).map((entry) => [String(entry.clientRowId), { [entry.field || 'number']: entry.errMsg }]));
  return { createdIds, errorsByRow };
};

/**
 * Whether a paste into an option-backed cell should leave the cell as it was: the text was not blank
 * but matched none of the cell's options. Writing that empty result wiped the cell — and on Factory
 * raised its removal dialog — for text that was never a value of this column. The grid's own select
 * paste keeps the current value in the same case; pasting a blank cell still clears.
 * @param options the options the cell resolves pasted labels against
 * @param text the pasted text
 * @param resolved what the text resolved to
 * @returns true when the cell's current value should be kept
 */
export const isUnmatchedPaste = (options: readonly unknown[] | undefined, text: unknown, resolved: unknown): boolean =>
  Boolean(options?.length) && String(text ?? '').trim() !== '' && (Array.isArray(resolved) ? resolved.length === 0 : resolved === '');
